import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle,
  Instagram,
  Globe,
  ArrowUpRight,
  Loader2,
  Send,
} from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import {
  WHATSAPP_DISPLAY,
  INSTAGRAM_HANDLE,
  INSTAGRAM_URL,
  SITE_URL,
  SITE_DOMAIN,
  waLink,
} from "../lib/site";

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: WHATSAPP_DISPLAY,
    href: waLink("Olá! Vim pelo site da EDALIX e quero conversar."),
    testid: "contact-whatsapp-button",
  },
  {
    icon: Instagram,
    label: "Instagram",
    value: INSTAGRAM_HANDLE,
    href: INSTAGRAM_URL,
    testid: "contact-instagram-button",
  },
  {
    icon: Globe,
    label: "Website",
    value: SITE_DOMAIN,
    href: SITE_URL,
    testid: "contact-website-button",
  },
];

const EMPTY = { name: "", email: "", phone: "", solution: "", message: "" };

const inputClass =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-[border-color,background-color] duration-300 focus:border-[#00E5FF]/60 focus:bg-white/[0.05]";

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      toast.success("Mensagem enviada com sucesso! A EDALIX retornará em breve.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Não foi possível enviar sua mensagem. Tente pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" data-testid="contact-section" className="relative py-24 lg:py-32">
      <div className="pointer-events-none absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-[#8A2BE2]/10 blur-[140px]" />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-5 lg:gap-16">
          <div className="lg:col-span-2">
            <SectionHeading
              eyebrow="CONTATO"
              title={
                <>
                  VAMOS CONSTRUIR ALGO <span className="text-gradient">JUNTOS?</span>
                </>
              }
              description="Escolha o canal de sua preferência ou envie sua necessidade pelo formulário. Respondemos rapidamente."
            />
            <div className="mt-10 space-y-3">
              {CHANNELS.map((c, i) => (
                <Reveal key={c.label} delay={0.08 * i}>
                  <a
                    href={c.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={c.testid}
                    className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-[border-color,background-color] duration-300 hover:border-[#00E5FF]/40 hover:bg-white/[0.05]"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#00E5FF]/20 to-[#8A2BE2]/20 ring-1 ring-white/15">
                      <c.icon className="h-5 w-5 text-[#00E5FF]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-semibold tracking-[0.25em] text-white/40">
                        {c.label.toUpperCase()}
                      </span>
                      <span className="mt-0.5 block truncate text-sm font-medium text-white/85">
                        {c.value}
                      </span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-white/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#00E5FF]" />
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="gradient-border rounded-3xl p-7 sm:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
                    NOME
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    required
                    minLength={2}
                    maxLength={120}
                    placeholder="Seu nome"
                    value={form.name}
                    onChange={set("name")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
                    E-MAIL
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    maxLength={160}
                    placeholder="voce@empresa.com"
                    value={form.email}
                    onChange={set("email")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
                    TELEFONE / WHATSAPP
                  </label>
                  <input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    maxLength={30}
                    placeholder="(49) 99999-9999"
                    value={form.phone}
                    onChange={set("phone")}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-solution" className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
                    TIPO DE SOLUÇÃO
                  </label>
                  <select
                    id="contact-solution"
                    data-testid="contact-solution-select"
                    required
                    value={form.solution}
                    onChange={set("solution")}
                    className={`${inputClass} appearance-none`}
                  >
                    <option value="" disabled>
                      Selecione uma opção
                    </option>
                    <option value="Site">Site</option>
                    <option value="Sistema">Sistema</option>
                    <option value="Aplicativo">Aplicativo</option>
                    <option value="IA & Automação">IA &amp; Automação</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold tracking-[0.2em] text-white/50">
                    MENSAGEM
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    minLength={10}
                    maxLength={2000}
                    rows={5}
                    placeholder="Conte o que sua empresa precisa..."
                    value={form.message}
                    onChange={set("message")}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-8 py-4 text-sm font-semibold text-white shadow-[0_14px_44px_-12px_rgba(0,229,255,0.55)] transition-[transform,filter,opacity] duration-300 hover:scale-[1.01] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Enviar mensagem
                    <Send className="h-4 w-4" />
                  </>
                )}
              </button>
              <p className="mt-4 text-center text-xs font-light text-white/45">
                Seus dados são usados apenas para retornarmos o contato.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
